import { httpAuthorized } from "@/main";
import { handleRequest } from "..";
import {
  DeviceUpgradePlan,
  EndpointUpgradePlan,
  Svc004UpgradeItem,
} from "@/types/operation";
import { BasePageParams, IPage } from "@/types/base";

export const createDeviceUpgradePlanApi = async (params: {
  shipId: string;
  typeId: number;
  planTime: string;
  plans: {
    typeId: number;
    positions: number[][];
    fileId: string;
  }[];
}): Promise<boolean | null> => {
  return handleRequest<boolean>(
    httpAuthorized.post("/mqtt/createDeviceUpgradePlan", {
      ...params,
    }),
    (data) => data.data.result
  );
};

// Svc001 新协议终端升级（三文件：固件 + MD5 校验文件 + 签名文件）
export const createEndpointUpgradePlanApi = async (params: {
  planName: string;
  endpointId: string;
  shipId: string;
  fileId: string;
  fileName: string;
  filePath: string;
  md5FileId: string;
  md5FileName: string;
  md5FilePath: string;
  signFileId: string;
  signFileName: string;
  signFilePath: string;
  planTime: string;
}): Promise<boolean | null> => {
  return handleRequest<boolean>(
    httpAuthorized.post("/mqtt/createEndpointUpgradePlan", {
      ...params,
    }),
    (data) => data.data.result
  );
};

export const getDeviceUpgradePlanListApi = async (
  params: BasePageParams
): Promise<IPage<DeviceUpgradePlan> | null> => {
  return handleRequest<IPage<DeviceUpgradePlan>>(
    httpAuthorized.get("/mqtt/getDeviceUpgradeList", {
      params: params,
    }),
    (data) => data.data.result
  );
};

export const getEndpointUpgradePlanListApi = async (
  params: BasePageParams
): Promise<IPage<EndpointUpgradePlan> | null> => {
  return handleRequest<IPage<EndpointUpgradePlan>>(
    httpAuthorized.get("/mqtt/getEndpointUpgradeList", {
      params: params,
    }),
    (data) => data.data.result
  );
};

export const cancelEndpointUpgradePlanApi = async (
  id: string
): Promise<boolean | null> => {
  return handleRequest<boolean>(
    httpAuthorized.post("/mqtt/cancelEndpointUpgradePlan", {
      id: id,
    }),
    (data) => data.data.result
  );
};

export const cancelDeviceUpgradePlanApi = async (
  id: string
): Promise<boolean | null> => {
  return handleRequest<boolean>(
    httpAuthorized.post("/mqtt/cancelDeviceUpgradePlan", {
      id: id,
    }),
    (data) => data.data.result
  );
};

// ==================== Svc004 新协议设备升级（task + item 架构） ====================

// Svc004 - 创建升级任务（含升级项列表）
export const createSvc004UpgradeTaskApi = async (params: {
  planName: string;
  endpointId: string;
  shipId: string;
  planTime: string;
  items: {
    devName: string;
    subName: string;
    subLv: number;
    subType: number;
    subId: string[];
    fileId: string;
    fileName: string;
    filePath: string;
    fileMd5: string;
  }[];
}): Promise<boolean | null> => {
  return handleRequest<boolean>(
    httpAuthorized.post("/mqtt/svc004/createUpgradeTask", {
      ...params,
    }),
    (data) => data.data.result
  );
};

// Svc004 - 获取升级任务列表
export const getSvc004UpgradeListApi = async (
  params: BasePageParams
): Promise<IPage<DeviceUpgradePlan> | null> => {
  return handleRequest<IPage<DeviceUpgradePlan>>(
    httpAuthorized.get("/mqtt/svc004/getUpgradeList", {
      params: params,
    }),
    (data) => data.data.result
  );
};

// Svc004 - 获取升级任务下的升级项列表（展开行）
export const getSvc004UpgradeItemsApi = async (
  taskId: string
): Promise<Svc004UpgradeItem[] | null> => {
  return handleRequest<Svc004UpgradeItem[]>(
    httpAuthorized.get("/mqtt/svc004/getUpgradeItems", {
      params: { taskId },
    }),
    (data) => data.data.result
  );
};

// Svc004 - 取消升级任务
export const cancelSvc004UpgradeTaskApi = async (
  id: string
): Promise<boolean | null> => {
  return handleRequest<boolean>(
    httpAuthorized.post("/mqtt/svc004/cancelUpgradeTask", {
      id: id,
    }),
    (data) => data.data.result
  );
};

// ==================== Svc001 终端固件查询（[1,4]/[1,5]） ====================

/**
 * 终端固件信息（固件文件名称）
 * queryStatus: 0=库内快照 1=实时查询成功 2=超时 3=终端错误 4=离线
 */
export interface EndpointFirmwareInfo {
  endpointId: string;
  firmware: string | null;
  firmwareReportTime: string | null;
  online: boolean;
  queryStatus: number;
  message: string | null;
}

/**
 * 批量查询终端固件名
 * @param endpointIds 终端 ID 列表（自动去重）
 * @param force true=对在线终端实时下发 [1,4] 查询并更新快照；false=只读库内快照
 */
export const queryEndpointFirmwareApi = async (
  endpointIds: string[],
  force: boolean
): Promise<EndpointFirmwareInfo[] | null> => {
  return handleRequest<EndpointFirmwareInfo[]>(
    httpAuthorized.post("/mqtt/svc001/queryFirmware", {
      endpointIds,
      force,
    }),
    (data) => data.data.result || []
  );
};

// ==================== 统一推送记录 ====================

export interface PushRecord {
  id: string;
  type: number; // 1=终端升级 2=设备升级 3=终端协议 4=设备协议
  refId: string;
  planName: string;
  shipId: string;
  shipName: string;
  endpointId: string;
  needConsent: number;
  customerAttitude: number;
  pushTime: string;
  pushStatus: number;
  responseTime: string;
  responseStatus: number;
  resultTime: string;
  resultStatus: number; // 0=进行中 1=成功 2=失败 3=已取消 4=超时
  totalItems: number;
  successItems: number;
  detail: string;
  extraJson: string;
  fileName?: string;
  filePath?: string;
  fileMd5?: string;
  fileSize?: number;
  managerId: string;
  createtime: string;
}

export interface PushRecordItem {
  id: string;
  recordId: string;
  sequence: number;
  devName: string;
  subName: string;
  subType: number;
  subId: string;
  fileName: string;
  filePath: string;
  fileMd5: string;
  fileSize: number;
  pushStatus: number;
  responseStatus: number;
  resultStatus: number;
  errorCode: number;
  extraJson: string;
}

export const getPushRecordListApi = async (params: {
  current: number;
  size: number;
  type?: number;
  shipId?: string;
}): Promise<IPage<PushRecord> | null> => {
  return handleRequest<IPage<PushRecord>>(
    httpAuthorized.get("/pushRecord/list", {
      params: params,
    }),
    (data) => {
      const r = data.data;
      return {
        records: r.records || [],
        total: r.total || 0,
        current: r.current || 1,
        size: r.size || 20,
      } as IPage<PushRecord>;
    }
  );
};

export const getPushRecordItemsApi = async (
  recordId: string
): Promise<PushRecordItem[] | null> => {
  return handleRequest<PushRecordItem[]>(
    httpAuthorized.get(`/pushRecord/items/${recordId}`),
    (data) => data.data.items || []
  );
};

export const cancelPushRecordApi = async (
  recordId: string
): Promise<boolean | null> => {
  return handleRequest<boolean>(
    httpAuthorized.post(`/pushRecord/cancel/${recordId}`),
    (data) => data.data.result
  );
};
